import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { CreateOrUpdateInvoiceHandler } from '../types';
import { ButtonBarButtonsProp } from '../../ButtonBar';
import { Icon } from '../../../blocks/icons';
import { useBlockingRouter } from '../../../hooks/useBlockingRouter';
import {
	InvoiceStatus,
	UseInvoiceEditButtonBarSaveButtonInvoiceFragment,
} from '#types';
import useContactIsChanging from '../../../hooks/useContactIsChanging';
import { useToast } from '../../../hooks/useToast';
import { INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID } from '../../../lib/editorBlockerIds';

export const useInvoiceEditButtonBarSaveButtonInvoiceFragment = gql`
	fragment useInvoiceEditButtonBarSaveButtonInvoice on Invoice {
		id
		status
		contact {
			id
		}
	}
`;

type useInvoiceEditButtonBarSaveButtonProps = {
	invoice?: Omit<UseInvoiceEditButtonBarSaveButtonInvoiceFragment, 'id'> & {
		id?: string;
	};
	onCreateOrUpdateHandler: CreateOrUpdateInvoiceHandler;
	disabled?: boolean;
	setContactOptional: React.Dispatch<React.SetStateAction<boolean>>;
};

type useInvoiceEditButtonBarSaveButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
};

export const useInvoiceEditButtonBarSaveButton = ( {
	invoice,
	onCreateOrUpdateHandler,
	disabled,
	setContactOptional,
}: useInvoiceEditButtonBarSaveButtonProps ): useInvoiceEditButtonBarSaveButtonReturnType => {
	const { showSuccess } = useToast();
	// we'll need the invoice from the API, not from local state
	const hideSaveButtonStates = [
		InvoiceStatus.Hidden,
		InvoiceStatus.Void,
		InvoiceStatus.Disputed,
	];
	const shouldRenderSaveButton =
		!invoice || !hideSaveButtonStates.includes( invoice?.status );
	const router = useBlockingRouter();
	const [ loading, setLoading ] = useState( false );
	const isSavingDraft =
		!invoice?.status || invoice.status == InvoiceStatus.Draft;
	const buttonName = isSavingDraft ? 'Save Draft' : 'Save';
	const contactIdForSentInvoiceDiffersFromCurrent = useContactIsChanging(
		invoice?.contact?.id
	);
	return {
		button: {
			[ buttonName ]: {
				isVisible: shouldRenderSaveButton,
				isIconWhenSmall: true,
				'data-cy': 'save-button',
				startIcon: <Icon type='save' />,
				loading,
				disabled:
					disabled ||
					( contactIdForSentInvoiceDiffersFromCurrent &&
						invoice &&
						invoice.status !== InvoiceStatus.Draft ),
				// Since the state lives above us, we pass in a handler from above
				onClick: async () => {
					if ( isSavingDraft ) {
						setContactOptional( ( prevState ) => ( prevState ? prevState : true ) );
						await new Promise( ( resolve ) => setTimeout( resolve, 0 ) ); // wait a beat for a render cycle for state to update
					}
					setLoading( true );
					const success = await onCreateOrUpdateHandler( {
						intendToSend: false,
					} );
					if ( success ) {
						if ( success.status === InvoiceStatus.Draft ) {
							showSuccess( 'Invoice draft saved' );
						} else {
							showSuccess( 'Invoice successfully updated' );
						}
						// on success redirect to non-edit detail page
						await router.push( `/invoice/${ success.id }?tab=view`, {
							overrideBlockers: [ INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID ],
						} );
					}
					setLoading( false );
				},
			},
		},
	};
};
